import React, { useState } from "react";
import {
    Box,
    Button,
    Divider,
    Grid,
    Paper,
    Tab,
    Tabs,
    Typography,
    CircularProgress
} from "@mui/material";
import EditIcon from '@mui/icons-material/Edit';
import { TabContext, TabPanel } from "@mui/lab";
import { MetaDrivenTable, MapBox, Data, DataLayout, PaginationControls, DialogBox, DrawerControl } from "../../../shared/components";
import { formatDate } from "../../../shared/utils/formatDate";
import { useProject } from "../hooks/useProject";
import { useAssetDetails } from "../hooks/useAssetDetails";
import { useStakeHolder } from "../hooks/useStakeHolder";
import { useParams } from "react-router-dom";
import { useCapabilities } from "../../../hooks/useCapabilities";
import { Asset, StakeHolder } from "../components";
import { AssetProps } from "../components/Asset";
import { useNavigation } from "../../../hooks/useNavigation";

export const Overview: React.FC = () => {

    const { can } = useCapabilities();

    const isAllowedToEditProject = can("operational_delivery:project_manager:project:edit");
    const isAllowedToAddStakeHolder = can("operational_delivery:project_overview:stakeholder:add");
    const isAllowedToAddAsset = can("operational_delivery:project_overview:asset:add");

    const { goToEditProject } = useNavigation();

    const { projectId } = useParams<{ projectId: string }>();

    const [currentTab, setCurrentTab] = useState("details");

    const handleChangeTab = (_e: React.SyntheticEvent, tab: string) => {
        setCurrentTab(tab);
    };

    const [isAddAsset, setIsAddAsset] = useState(false);
    const [isEditAsset, setIsEditAsset] = useState<{
        status: boolean;
        assetDetails: AssetProps | null;
    }>({
        status: false,
        assetDetails: null
    });

    const [isEditStakeHolder, setIsEditStakeHolder] = useState<{
        status: boolean;
        stakeHolderDetails: null | Record<string, any>;
    }>({
        status: false,
        stakeHolderDetails: null
    });

    const tabs = [
        { value: "details", label: "Project Details" },
        { value: "assets", label: "Assets" },
        { value: "stake-holders", label: "StakeHolders" }
    ];

    const { isPending: isProjectDetailsPending, projectData } = useProject(projectId);

    const { isPending: isAssestDetailsPending, assetData, assetDataPagination, changePage: changeAssetPage, changeRowsPerPage: changeAssetRowsPerPage, isRefetching: isAssetRefetching, refetch: refetchAsset } = useAssetDetails(projectId);
    const { isPending: isStakeHolderDetailsPending, stakeHolderData, stakeHolderDataPagination, changePage: changeStakeHolderPage, changeRowsPerPage: changeStakeHolderRowsPerPage } = useStakeHolder({ projectId, isRemovePagination: false });

    const assetHeaders = [
        { "alias_name": "asset_category", "label": "Asset Category", "domain": "", "display_order": 1, "is_show": true },
        { "alias_name": "narms_value", "label": "NARMS Values", "domain": "", "display_order": 2, "is_show": true },
        { "alias_name": "work_type_group", "label": "Work Type Group", "domain": "WORK_TYPE_GROUP", "display_order": 3, "is_show": true },
        { "alias_name": "work_type", "label": "Work Type", "domain": "WORK_TYPE", "display_order": 4, "is_show": true },
        { "alias_name": "functional_location", "label": "Functional Location", "domain": "", "display_order": 5, "is_show": true },
        { "alias_name": "asset_budget", "label": "Asset Budget", "domain": "", "display_order": 6, "is_show": true },
        { "alias_name": "asset_description", "label": "Asset Description", "domain": "", "display_order": 7, "is_show": true }
    ];

    const stakeHolderHeaders = [
        { "alias_name": "derived_name", "label": "Name", "domain": "PROJECT_STATUS", "display_order": 1, "is_show": true },
        { "alias_name": "email", "label": "Email", "domain": "PROJECT_STATUS", "display_order": 2, "is_show": true },
        { "alias_name": "name", "label": "Role Name", "domain": "", "display_order": 3, "is_show": true },
        { "alias_name": "created_at", "label": "Created Date", "domain": "", "display_order": 4, "is_show": true },
        { "alias_name": "description", "label": "Description", "domain": "", "display_order": 5, "is_show": true },
    ];

    const Loader = () => {
        return (
            <Box
                height="100%"
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                width="100%"
                gap={2}
            >
                <CircularProgress />
            </Box>
        )
    }

    if (isProjectDetailsPending || isAssestDetailsPending || isStakeHolderDetailsPending) {
        return (
            <Loader />
        )
    };

    return (
        <Paper
            data-background-elevation={1}
            sx={{ overflow: "hidden", height: "100%", boxShadow: 'none', borderRadius: 0 }}
        >
            <Grid container spacing={2} p={2}>
                {/* LEFT PANEL */}
                <Grid size={3}>
                    <Paper
                        data-background-elevation={1}
                        sx={{ overflow: "hidden", height: "fit-content" }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                position: "relative",
                                height: { xs: 350, md: 300 },
                                minHeight: 350,
                            }}
                        >
                            <MapBox location={[projectData.longitude, projectData.latitude]} />
                        </Box>
                    </Paper>

                    <Box display={"flex"} flexDirection={"column"} gap={1.2} mt={2}>

                        <Typography variant="body1" color="text.primary">
                            {projectData.title}
                        </Typography>

                        <Divider />

                        {/* Reference and Location Info */}
                        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                            <Typography variant="body2" color="text.primary">Project Reference</Typography>
                            <Typography variant="body2" color="text.primary">{projectData.project_reference}</Typography>
                        </Box>

                        <Box
                            sx={{
                                display: "flex",
                                flexWrap: { xs: "wrap", md: "nowrap" },
                                justifyContent: "space-between",
                            }}
                            gap={2}
                        >
                            <Typography variant="body2" color="text.secondary">
                                {projectData.address}
                            </Typography>
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ flexShrink: 0 }}
                            >
                                {projectData.area_text}
                            </Typography>
                        </Box>

                        <Divider />

                        {[
                            { key: "description", label: "Description", content: projectData.description },
                            { key: "functional_location", label: "Functional Location", content: projectData.functional_location },
                            { key: "project_scope", label: "Project Scope", content: projectData.project_scope },
                            { key: "stage_description", label: "Stage Description", content: projectData.stage_description },
                            { key: "reporting_year", label: "Reporting Year", content: projectData.reporting_year },
                            { key: "status_display_text", label: "Project Status", content: projectData.status_display_text },
                        ].map((item, index) => (
                            <Data key={index} label={item.label} content={item.content} />
                        ))}

                    </Box>
                </Grid>

                {/* RIGHT PANEL */}
                <Grid size={9}>
                    <Box display="flex" flexDirection="column" height="100%" gap={1.5}>
                        <TabContext value={currentTab}>
                            <Box>
                                <Box display="flex" alignItems="center" alignContent="center">
                                    <Tabs
                                        value={currentTab}
                                        onChange={handleChangeTab}
                                        aria-label="Tabs"
                                        variant="scrollable"
                                        scrollButtons="auto"
                                        allowScrollButtonsMobile
                                        sx={{ minHeight: "auto" }}
                                    >
                                        {tabs.map((t) => (
                                            <Tab
                                                key={t.value}
                                                sx={{ height: "auto", minHeight: "auto" }}
                                                label={t.label}
                                                value={t.value}
                                            />
                                        ))}
                                    </Tabs>
                                    {
                                        isAllowedToEditProject && (
                                            <Button variant="text" sx={{ ml: "auto" }} onClick={() => { projectId && goToEditProject(projectId) }}>
                                                Edit project
                                            </Button>
                                        )
                                    }
                                    {
                                        currentTab === "stake-holders" && isAllowedToAddStakeHolder && (
                                            <Button variant="text" sx={{ ml: 1 }} disabled>
                                                Add StakeHolder
                                            </Button>
                                        )
                                    }
                                    {
                                        currentTab === "assets" && isAllowedToAddAsset && (
                                            <Button variant="text" sx={{ ml: 1 }} onClick={() => setIsAddAsset(true)}>
                                                Add Asset
                                            </Button>
                                        )
                                    }
                                </Box>
                                <Divider />
                            </Box>

                            <Box flex={1} overflow="hidden">
                                <TabPanel value="details" sx={{ p: 0, height: "100%" }}>
                                    <Box px={1}>
                                        <Grid container spacing={2}>
                                            <Grid size={12}>
                                                <DataLayout title="Details">
                                                    <Box display="flex" flexDirection="column" gap={1.5} p={2}>
                                                        {[
                                                            { key: "project_budget", label: "Project Budget", content: projectData.project_budget },
                                                            { key: "description", label: "Description", content: projectData.description },
                                                            { key: "project_scope", label: "Project Scope", content: projectData.project_scope },
                                                            { key: "workstream", label: "Workstream", content: projectData.workstream },
                                                            { key: "portfolio", label: "Portfolio", content: projectData.portfolio },
                                                            { key: "programme", label: "Programme", content: projectData.programme },
                                                        ].map((item, index) => (
                                                            <Data key={index} label={item.label} content={item.content} />
                                                        ))}
                                                    </Box>
                                                </DataLayout>
                                            </Grid>
                                            <Grid size={6}>
                                                <DataLayout title="Location Details">
                                                    <Box display="flex" flexDirection="column" gap={1.5} p={2}>
                                                        {[
                                                            { key: "county", label: "County", content: projectData.county },
                                                            { key: "address", label: "Address", content: projectData.address },
                                                            { key: "area_text", label: "Area", content: projectData.area_text },
                                                            { key: "site", label: "Site", content: projectData.site },
                                                            { key: "what3_words", label: "What3Words", content: projectData.what3_words },
                                                            { key: "network", label: "Network", content: projectData.network },
                                                        ].map((item, index) => (
                                                            <Data key={index} label={item.label} content={item.content} />
                                                        ))}
                                                    </Box>
                                                </DataLayout>
                                            </Grid>
                                            <Grid size={6}>
                                                <DataLayout title="Schedule Details">
                                                    <Box display="flex" flexDirection="column" gap={1.5} p={2}>
                                                        {[
                                                            { key: "planned_start_date", label: "Planned Start Date", content: formatDate(projectData.planned_start_date) },
                                                            { key: "planned_end_date", label: "Planned End Date", content: formatDate(projectData.planned_end_date) },
                                                            { key: "planned_commissioning_date", label: "Planned Commissioning Date", content: formatDate(projectData.planned_commissioning_date) },
                                                            { key: "planned_close_up_date", label: "Planned Close Up Date", content: formatDate(projectData.planned_close_up_date) },
                                                        ].map((item, index) => (
                                                            <Data key={index} label={item.label} content={item.content} />
                                                        ))}
                                                    </Box>
                                                </DataLayout>
                                            </Grid>
                                        </Grid>
                                    </Box>
                                </TabPanel>
                                <TabPanel value="assets" sx={{ p: 0, height: "100%" }}>
                                    {
                                        isAssetRefetching ?
                                            <Loader />
                                            :
                                            <>
                                                <MetaDrivenTable headers={assetHeaders} data={assetData} dataMetrics={[]} sortDisabled={true}
                                                    actions={[{
                                                        label: <EditIcon />,
                                                        handleClick: (row: AssetProps) => {
                                                            setIsEditAsset({
                                                                status: true,
                                                                assetDetails: row
                                                            });
                                                        }
                                                    }]}
                                                />
                                                <PaginationControls
                                                    total={assetDataPagination?.total}
                                                    rowsPerPage={assetDataPagination?.limit}
                                                    currentPage={assetDataPagination?.page}
                                                    handlePageChange={changeAssetPage}
                                                    handleRowsPerPage={changeAssetRowsPerPage}
                                                />
                                            </>

                                    }
                                </TabPanel>
                                <TabPanel value="stake-holders" sx={{ p: 0, height: "100%" }}>
                                    <MetaDrivenTable headers={stakeHolderHeaders} data={stakeHolderData} dataMetrics={{ "created_at": { type: "date" } }} sortDisabled={true}
                                        actions={[{
                                            label: <EditIcon />,
                                            handleClick: (row: Record<string, any>) => {
                                                setIsEditStakeHolder({
                                                    status: true,
                                                    stakeHolderDetails: row
                                                });
                                            }
                                        }]}
                                    />
                                    <PaginationControls
                                        total={stakeHolderDataPagination?.total}
                                        rowsPerPage={stakeHolderDataPagination?.limit}
                                        currentPage={stakeHolderDataPagination?.page}
                                        handlePageChange={changeStakeHolderPage}
                                        handleRowsPerPage={changeStakeHolderRowsPerPage}
                                    />
                                </TabPanel>
                            </Box>
                        </TabContext>
                    </Box>
                </Grid>
            </Grid>

            <DrawerControl
                isOpen={isEditAsset.status}
                title="Edit Asset"
                subTitle=""
                handleClose={() => setIsEditAsset({ status: false, assetDetails: null })}
                children={
                    isEditAsset.assetDetails && projectId &&
                    <Asset
                        projectId={projectId}
                        asset={isEditAsset.assetDetails}
                        onSuccess={() => {
                            refetchAsset();
                            setIsEditAsset({ status: false, assetDetails: null });
                        }}
                        onClose={() => setIsEditAsset({ status: false, assetDetails: null })}
                        isEdit={true}
                    />
                }
            />

            <DrawerControl
                isOpen={isAddAsset}
                title="Add Asset"
                subTitle=""
                handleClose={() => setIsAddAsset(false)}
                children={
                    projectId &&
                    <Asset
                        projectId={projectId}
                        onSuccess={() => {
                            setIsEditAsset({ status: false, assetDetails: null });
                            setIsAddAsset(false);
                            refetchAsset();
                        }}
                        onClose={() => setIsAddAsset(false)}
                        isEdit={false}
                    />
                }
            />

            <DialogBox
                showModal={isEditStakeHolder.status}
                title="Edit StakeHolder"
                subTitle=""
                onModalClose={() => setIsEditStakeHolder({ status: false, stakeHolderDetails: null })}
                children={<StakeHolder />}
            />

        </Paper>
    );
};